/**
 * A pill on a vertical's card: an `exam` (solid) or a `program` (dashed:
 * curricula, CE, item types and the like).
 */
export interface McGrawHillVerticalPill {
  name: string;
  kind: 'exam' | 'program';
}

export interface McGrawHillVerticalSection {
  /** Stored in sentence case; the card shows it uppercase. */
  label: string;
  pills: readonly McGrawHillVerticalPill[];
}

export interface McGrawHillVertical {
  key: string;
  /** The card's round badge, e.g. `MD`. */
  badge: string;
  title: string;
  description: string;
  sections: readonly McGrawHillVerticalSection[];
}

const exam = (name: string): McGrawHillVerticalPill => ({ name, kind: 'exam' });

const program = (name: string): McGrawHillVerticalPill => ({
  name,
  kind: 'program',
});

const section = (
  label: string,
  ...pills: McGrawHillVerticalPill[]
): McGrawHillVerticalSection => ({ label, pills });

/** A card's "N exams": its `exam` pills, so the count cannot drift. */
export const countMcGrawHillVerticalExams = ({
  sections,
}: McGrawHillVertical): number =>
  sections.reduce(
    (total, { pills }) =>
      total + pills.filter(({ kind }) => kind === 'exam').length,
    0
  );

/**
 * mcgraw-hill: the demo launcher's verticals (ORA-1264, from the owner's
 * mock), each a card, in the order the masonry columns read. The ones with a
 * demo are `MCGRAW_HILL_VERTICAL_SCOPES` (mcgrawHillVerticalScope.ts); the
 * rest do nothing.
 */
export const MCGRAW_HILL_VERTICALS: readonly McGrawHillVertical[] = [
  {
    key: 'medicine',
    badge: 'MD',
    title: 'Medicine',
    description: 'Pre-med through practice, MD and DO',
    sections: [
      section('Pre-med', exam('MCAT')),
      section(
        'Preclinical',
        program('Institutional curriculum, mapped'),
        exam('USMLE Step 1'),
        exam('COMLEX Level 1')
      ),
      section(
        'Clerkships',
        exam('Internal Medicine shelf'),
        exam('Surgery shelf'),
        exam('Pediatrics shelf'),
        exam('OB/GYN shelf'),
        exam('Psychiatry shelf'),
        exam('Neurology shelf'),
        exam('Family Medicine shelf'),
        exam('Emergency Medicine shelf'),
        exam('COMAT')
      ),
      section(
        'Licensure',
        exam('USMLE Step 2 CK'),
        exam('COMLEX Level 2'),
        exam('USMLE Step 3'),
        exam('COMLEX Level 3')
      ),
      section(
        'Residency and boards',
        exam('In-training exams'),
        exam('ABIM'),
        exam('ABFM'),
        exam('ABP'),
        exam('ABS'),
        exam('ABEM'),
        exam('ABA'),
        exam('ABOG'),
        exam('ABPN')
      ),
      section('Practice', program('CME'), program('MOC recertification')),
    ],
  },
  {
    key: 'nursing',
    badge: 'RN',
    title: 'Nursing',
    description: 'Pre-licensure through advanced practice',
    sections: [
      section(
        'Licensure',
        exam('NCLEX-RN'),
        exam('NCLEX-PN'),
        program('Next Gen item types')
      ),
      section(
        'Programs',
        program('BSN courses, mapped'),
        program('ADN courses, mapped'),
        program('LPN/LVN courses, mapped'),
        program('Exit and readiness exams')
      ),
      section(
        'Advanced practice nursing (APN)',
        exam('FNP (AANP)'),
        exam('FNP (ANCC)'),
        exam('AGNP'),
        exam('PMHNP'),
        exam('CRNA (NCE)'),
        exam('Midwifery (AMCB)'),
        exam('CNS (ANCC)')
      ),
      section('Practice', program('Nursing CE')),
    ],
  },
  {
    key: 'allied-health',
    badge: 'AH',
    title: 'Allied Health',
    description: 'Career colleges and certificate programs',
    sections: [
      section(
        'Certification',
        exam('Medical assisting (CMA, RMA, CCMA)'),
        exam('EMT and Paramedic (NREMT)'),
        exam('Surgical tech (CST)'),
        exam('Pharmacy tech (PTCE, ExCPT)'),
        exam('Dental assisting (CDA)'),
        exam('Radiologic tech (ARRT)'),
        exam('Sonography (ARDMS)'),
        exam('Respiratory therapy (TMC, CSE)'),
        exam('Lab (MLT, MLS)'),
        exam('CNA'),
        exam('Phlebotomy (CPT)'),
        exam('Dietetics (CDR RDN)')
      ),
      section('Programs', program('Program courses, mapped')),
    ],
  },
  {
    key: 'pa',
    badge: 'PA',
    title: 'Physician Assistant',
    description: 'Didactic year, clinical year, certification',
    sections: [
      section('Certification', exam('PANCE'), exam('PANRE'), exam('PANRE-LA')),
      section(
        'Programs',
        exam('PAEA End of Rotation exams'),
        program('PA curriculum, mapped')
      ),
    ],
  },
  {
    key: 'rehab',
    badge: 'PT',
    title: 'Rehabilitation',
    description: 'Physical therapy, occupational therapy, speech',
    sections: [
      section(
        'Licensure',
        exam('NPTE'),
        exam('NPTE-PTA'),
        exam('NBCOT OTR'),
        exam('NBCOT COTA'),
        exam('Praxis SLP'),
        exam('BOC athletic training')
      ),
      section('Programs', program('DPT and OTD curricula, mapped')),
    ],
  },
  {
    key: 'pharmacy',
    badge: 'RPh',
    title: 'Pharmacy',
    description: 'PharmD programs and licensure',
    sections: [
      section('Licensure', exam('NAPLEX'), exam('MPJE'), exam('CPJE')),
      section(
        'Programs',
        program('PharmD curriculum, mapped'),
        program('Pharmacy CE')
      ),
    ],
  },
  {
    key: 'behavioral-health',
    badge: 'BH',
    title: 'Behavioral Health',
    description: 'Psychology, social work, counseling, behavior analysis',
    sections: [
      section(
        'Licensure',
        exam('Psychology (EPPP)'),
        exam('Social work (ASWB)'),
        exam('Counseling (NCE, NCMHCE)'),
        exam('Behavior analysis (BCBA)')
      ),
      section('Programs', program('MSW and PsyD curricula, mapped')),
    ],
  },
  {
    key: 'dental',
    badge: 'DDS',
    title: 'Dental',
    description: 'Admission, dental school, hygiene',
    sections: [
      section('Exams', exam('DAT'), exam('INBDE'), exam('NBDHE')),
      section('Programs', program('Dental school curriculum, mapped')),
    ],
  },
  {
    key: 'vet',
    badge: 'DVM',
    title: 'Veterinary',
    description: 'Vet school and vet tech',
    sections: [
      section('Licensure', exam('NAVLE'), exam('VTNE')),
      section(
        'Programs',
        program('Vet school curriculum, mapped'),
        program('Pre-vet')
      ),
    ],
  },
  {
    key: 'other-licensed',
    badge: 'OD',
    title: 'Optometry, Podiatry, Chiropractic',
    description: 'Doctoral programs with their own boards',
    sections: [
      section(
        'Licensure',
        exam('Optometry (NBEO)'),
        exam('Podiatry (APMLE)'),
        exam('Chiropractic (NBCE)')
      ),
    ],
  },
  {
    key: 'international',
    badge: 'INT',
    title: 'International',
    description: 'Licensing exams outside the US, by country',
    sections: [
      section('India', exam('NEET-PG'), exam('FMGE')),
      section(
        'United Kingdom',
        exam('UKMLA'),
        exam('PLAB 1 and 2'),
        exam('NMC Test of Competence')
      ),
      section('Australia', exam('AMC MCQ'), exam('AMC Clinical')),
      section('Canada', exam('MCCQE Part I'), exam('CPNRE')),
      section('Philippines', exam('PLE'), exam('PNLE')),
      section('Indonesia', exam('UKMPPD')),
      section(
        'Programs',
        program('Curricula mapped for schools outside the US')
      ),
    ],
  },
];

/**
 * The launcher's three columns on a wide screen, top to bottom, as the
 * owner's mock places them (ORA-1264); narrower screens use the list order.
 */
export const MCGRAW_HILL_LAUNCHER_COLUMNS: readonly (readonly string[])[] = [
  ['medicine', 'pa', 'dental', 'vet'],
  ['nursing', 'rehab', 'other-licensed', 'behavioral-health'],
  ['allied-health', 'pharmacy', 'international'],
];
